import React, { useState, useEffect, useMemo } from "react";

import {
  createAttendancesForParticipants,
  fetchAttendances,
} from "@services/api";

import { useAppStore } from "@store/appStateStore";

import { sortParticipantsByName } from "../utils/sort";
import BaseField from "@components/shared/control/field/BaseField";
import { BaseOption } from "@components/shared/control/select/BaseSelect";
import ListSelect from "@components/shared/control/select/ListSelect";
import Container from "@components/shared/Container";
import BaseButton from "@components/shared/control/button/BaseButton";

const AddAttendanceForParticipantsForm: React.FC<{
  hasIsPaid?: boolean;
  hasAmount?: boolean;
}> = ({ hasIsPaid, hasAmount }) => {
  const { participants, directions, setAttendances } = useAppStore();

  const [date, setDate] = useState<string>();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [parts, setParts] = useState<BaseOption[]>([]);
  const [selectedParts, setSelectedParts] = useState<BaseOption[]>([]);

  const [selectedDirId, setSelectedDirId] = useState<number>(1);
  const [dirs, setDirs] = useState<BaseOption[]>([]);

  const [isPaid, setIsPaid] = useState<boolean>(true);
  const [amount, setAmount] = useState<number>(0);

  // Вычисляем unselectedParts с помощью useMemo
  const unselectedParts = useMemo(() => {
    return parts
      .filter((p) => {
        const v = String(p.value).toUpperCase();
        const s = String(searchQuery).toUpperCase();
        return v.includes(s);
      })
      .filter((np) => !selectedParts.some((sp) => np.key === sp.key));
  }, [searchQuery, parts, selectedParts]);

  const onSubmitFunc = async (e: any) => {
    e.preventDefault();

    const participantIds = selectedParts.map((sp) => sp.key);

    if (!date || participantIds.length === 0) {
      return;
    }

    await createAttendancesForParticipants(
      participantIds,
      date,
      isPaid,
      Number(amount),
      selectedDirId,
    );

    setAttendances(await fetchAttendances());
    setSelectedParts([]);
  };

  const onKeyDownFunc = (e: any) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const searchInputOnChangeFunc = (e: any) => setSearchQuery(e.target.value);

  const amountInputOnChangeFunc = (e: any) => {
    console.log(e);
    setIsPaid(e.target.value !== "0");
    setAmount(e.target.value);
  };

  const isPaidInputOnChangeFunc = (e: any) => {
    setIsPaid(e.target.checked);
  };

  const unselectedPartsOnClick = (e: any) => {
    const cp = parts.find((p) => Number(p.key) === Number(e.target.value));
    setSelectedParts(cp ? [...selectedParts, cp] : selectedParts);
  };

  const selectedPartsOnClick = (e: any) => {
    const cp = parts.find((p) => Number(p.key) === Number(e.target.value));
    setSelectedParts(selectedParts.filter((sp) => cp?.key !== sp.key));
  };

  const dateInputOnChangeFunc = (e: any) => setDate(e.target.value);
  const dirSelectOnChangeFunc = (dirId: number) => setSelectedDirId(dirId);

  useEffect(() => {
    const newDirOptions = directions.map((d) => {
      return { key: d.id, value: d.name };
    });
    setDirs(newDirOptions);
    setSelectedDirId(newDirOptions[0]?.key || 0);
  }, [directions]);

  useEffect(() => {
    const newPartOptions = sortParticipantsByName(participants).map((d) => {
      return { key: d.id, value: d.name };
    });
    setParts(newPartOptions);
  }, [participants]);

  return (
    <form
      className="attendance-form"
      onSubmit={onSubmitFunc}
      onKeyDown={onKeyDownFunc}
    >
      <div className="form-row" style={{ paddingBottom: "1rem" }}>
        <BaseField
          id={"dateInput"}
          type={"date"}
          label={"Дата занятия:"}
          value={date}
          onChange={dateInputOnChangeFunc}
          required={true}
        />
        <BaseField
          id={"directionSelect"}
          type={"select"}
          label={"Направление:"}
          value={selectedDirId}
          onChange={dirSelectOnChangeFunc}
          options={dirs}
          required={true}
        />
        <BaseField
          id={"searchInput"}
          type={"text"}
          label={"Поиск участника:"}
          value={searchQuery}
          onChange={searchInputOnChangeFunc}
        />
      </div>
      {(hasAmount || hasIsPaid) && (
        <div className="form-row" style={{ paddingBottom: "1rem" }}>
          {hasAmount && (
            <BaseField
              id={"amountInput"}
              type={"number"}
              label={"Стоимость:"}
              value={amount}
              onChange={amountInputOnChangeFunc}
              direction="horisontal"
            />
          )}
          {hasIsPaid && (
            <BaseField
              id={"isPaidCheckbox"}
              type={"checkbox"}
              label={"За деньги"}
              checked={isPaid}
              onChange={isPaidInputOnChangeFunc}
              direction="horisontal"
            />
          )}
        </div>
      )}
      <div className="container-row">
        <Container>
          <ListSelect
            id={"unselectedParticipants"}
            options={unselectedParts}
            onClick={unselectedPartsOnClick}
          />
        </Container>

        <Container>
          <ListSelect
            id={"selectedParticipants"}
            options={selectedParts}
            onClick={selectedPartsOnClick}
          />
        </Container>
      </div>

      <BaseButton
        id={"submitAttendanceForm"}
        type="submit"
        disabled={selectedParts.length === 0 || !date}
        label="Добавить занятие"
      />
    </form>
  );
};

export default AddAttendanceForParticipantsForm;
